import { Field, useFormikContext } from 'formik';
import { forwardRef, useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { DatePickerInput } from '@hivebrite/design-system/Inputs';

import { shouldDisplayError } from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import { type DateProps } from './types';
import { validateDate } from './validate';

export const Date = forwardRef<HTMLInputElement, DateProps>(
  (
    {
      ariaDescribedBy,
      ariaLabel,
      clearable = true,
      disabled = false,
      displayFormat,
      enableTime,
      hideError,
      icon,
      id,
      label,
      labelMultiline,
      locale,
      maxDate,
      minDate,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      tooltip,
      validate: { required } = {},
      wrapperProps = {},
      isStatic = false,
    },
    ref,
  ) => {
    const { setFieldTouched, setFieldValue } = useFormikContext();
    const htmlId = id || name;

    const handleBlur = useCallback(
      (date: string) => {
        setFieldTouched(name, true, false);
        onBlur?.(date);
      },
      [name, onBlur, setFieldTouched],
    );

    const handleChange = useCallback(
      (value: string) => {
        setFieldValue(name, value);
        onChange?.(value);
      },
      [name, onChange, setFieldValue],
    );

    const handleValidate = useCallback(
      (value: string) => validateDate({ required, value }),
      [required],
    );

    return (
      <Box mb={6} {...wrapperProps}>
        <Field name={name} validate={handleValidate}>
          {({ field, form, meta }) => (
            <DatePickerInput
              {...field}
              ariaDescribedBy={ariaDescribedBy}
              ariaLabel={ariaLabel}
              dataTestId="formik-date"
              disabled={disabled}
              displayFormat={displayFormat}
              clearable={clearable}
              enableTime={enableTime}
              error={shouldDisplayError({ meta, form })}
              hideError={hideError}
              icon={icon}
              id={htmlId}
              initialValue={meta.initialValue}
              label={label}
              labelMultiline={labelMultiline}
              locale={locale || I18n.locale}
              maxDate={maxDate}
              minDate={minDate}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={onFocus}
              placeholder={placeholder}
              ref={ref}
              required={required}
              tooltip={tooltip}
              isStatic={isStatic}
            />
          )}
        </Field>
      </Box>
    );
  },
);

export default Date;
